// src/components/login.js
// HYBRID AUTH: Password locally, Microsoft in Azure

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './login.module.css';
import logoAuth from '../Images/Machina.png';
import axios from 'axios';
import useAuth from '../hooks/useAuth.ts';

const SignInForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');
  const [authMode, setAuthMode] = useState(null); // 'password' or 'microsoft'
  const [email, setEmailInput] = useState('');
  const [password, setPassword] = useState('');
  
  const {
    authenticated,
    setAuthenticated,
    setName,
    setEmail,
    setRoles,
    setOrganizationIds,
  } = useAuth();

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const frontendUrl = process.env.REACT_APP_FRONTEND_URL;
  const from = location.state?.from?.pathname || '/landing';

  useEffect(() => {
    // If already authenticated, redirect
    if (authenticated) {
      navigate(from, { replace: true });
      return;
    }

    // Check for token in URL hash (from Easy Auth redirect)
    const hash = window.location.hash;
    if (hash && hash.includes('token=')) {
      handleTokenFromUrl(hash);
    } else {
      // Normal auth check
      initializeAuth();
    }
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (authenticated) {
      navigate(from, { replace: true });
    }
  }, [authenticated, navigate, from]);

  const initializeAuth = async () => {
    try {
      // Get auth mode from backend first
      const statusResponse = await axios.get(`${backendUrl}/api/auth/status`, {
        withCredentials: true
      });
      setAuthMode(statusResponse.data.auth_mode);
      
      // Check URL for error parameters (from failed OAuth)
      const params = new URLSearchParams(window.location.search);
      if (params.get('error')) {
        setError(params.get('error_description') || 'Authentication failed');
        setChecking(false);
        return;
      }

      // Now check if user is already authenticated
      await checkEasyAuthStatus();
      
    } catch (e) {
      console.error('Failed to get auth mode:', e);
      // Default to password if we can't reach backend
      setAuthMode('password');
      setChecking(false);
    }
  };

  const handleTokenFromUrl = async (hash) => {
    try {
      // Get auth mode first
      try {
        const statusResponse = await axios.get(`${backendUrl}/api/auth/status`, {
          withCredentials: true
        });
        setAuthMode(statusResponse.data.auth_mode);
      } catch (e) {
        setAuthMode('password');
      }

      // Extract token from URL hash
      const tokenMatch = hash.match(/token=([^&]+)/);
      if (tokenMatch) {
        const encodedToken = tokenMatch[1];
        const decodedToken = JSON.parse(decodeURIComponent(encodedToken));
        
        // Exchange the Easy Auth token for your app JWT
        await exchangeTokenForJWT(decodedToken.authenticationToken);
        
        // Clean the URL to remove the token
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      } else {
        await checkEasyAuthStatus();
      }
    } catch (error) {
      console.error('Error parsing token from URL:', error);
      await checkEasyAuthStatus();
    }
  };

  const exchangeTokenForJWT = async (easyAuthToken) => {
    try {
      // Exchange Easy Auth token for app JWT
      const { data } = await axios.post(
        `${backendUrl}/api/auth/session`,
        { token: easyAuthToken },
        { withCredentials: true }
      );
      
      if (data.authenticated) {
        handleAuthSuccess(data);
      } else {
        setError('Authentication failed. Please try again.');
        setChecking(false);
      }
    } catch (error) {
      console.error('Token exchange failed:', error);
      
      // Check if this is a "user not provisioned" error
      if (error.response?.status === 403) {
        const data = error.response.data;
        if (data.message && data.message.includes('not provisioned')) {
          navigate('/user-not-provisioned', { 
            state: {
              email: data.email,
              message: data.message
            },
            replace: true 
          });
          return;
        }
      }
      
      // If token exchange fails for other reasons, try normal auth check
      await checkEasyAuthStatus();
    }
  };

  const checkEasyAuthStatus = async () => {
    try {
      // First check if user has a valid JWT token
      const token = localStorage.getItem('token');
      
      if (token) {
        // Set the token in axios headers
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Validate the JWT
        const { data } = await axios.get(
          `${backendUrl}/api/auth/me`
        );
        
        if (data.authenticated) {
          // Token is valid, redirect to the app
          handleAuthSuccess(data);
          return;
        }
      }
      
      // No valid JWT, check if there's an Easy Auth session
      // This might fail due to CORS, which is okay - it means user needs to login
      try {
        const easyAuthResponse = await axios.get(
          `${backendUrl}/.auth/me`,
          { withCredentials: true }
        );
        
        if (easyAuthResponse.data && easyAuthResponse.data.length > 0) {
          // Get the access token from Easy Auth
          const accessToken = easyAuthResponse.data[0].access_token;
          
          // Exchange it for JWT
          try {
            const sessionResponse = await axios.post(
              `${backendUrl}/api/auth/session`,
              { token: accessToken },
              { withCredentials: true }
            );
            
            if (sessionResponse.data.authenticated) {
              handleAuthSuccess(sessionResponse.data);
              return;
            }
          } catch (sessionError) {
            // Check if this is a "user not provisioned" error
            if (sessionError.response?.status === 403) {
              const data = sessionError.response.data;
              if (data.message && data.message.includes('not provisioned')) {
                navigate('/user-not-provisioned', { 
                  state: {
                    email: data.email,
                    message: data.message
                  },
                  replace: true 
                });
                return;
              }
            }
            throw sessionError;
          }
        }
      } catch (easyAuthError) {
        // This is expected if no Easy Auth session exists
        console.log('No Easy Auth session found');
      }
      
      // No authentication found
      setChecking(false);
      
    } catch (error) {
      console.log('User not authenticated');
      localStorage.clear();
      delete axios.defaults.headers.common['Authorization'];
      setChecking(false);
    }
  };

  const handleAuthSuccess = (data) => {
    const { username, email, roles, organizationIds, jwt } = data;

    // Store in auth context
    setAuthenticated(true);
    setName(username);
    setEmail(email);
    setRoles(roles);
    setOrganizationIds(organizationIds);

    // Store in localStorage
    if (jwt) {
      localStorage.setItem('token', jwt);
    }
    localStorage.setItem('isLoggedIn', JSON.stringify(true));
    localStorage.setItem('name', username);
    localStorage.setItem('email', email);
    localStorage.setItem('roles', JSON.stringify(roles));
    localStorage.setItem('organizationIds', JSON.stringify(organizationIds));

    // Set axios default header
    if (jwt) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
    }

    // Navigate to the intended page
    navigate(from, { replace: true });
  };

  // Handle password login (local only)
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await axios.post(`${backendUrl}/login`, { 
        email, 
        password 
      });
      
      handleAuthSuccess(response.data);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.auth_mode === 'microsoft') {
        setError('Password login is disabled in production. Please use Microsoft authentication.');
        setAuthMode('microsoft');
      } else {
        setError(err.response?.data?.message || 'Login failed. Please try again.');
      }
    }
  };

  const handleMicrosoftLogin = () => {
    // Use the backend App Service URL directly for authentication
    const authUrl = `${backendUrl}/.auth/login/aad`;
    
    // Redirect back to the FRONTEND pad-data route
    const redirectUrl = `${frontendUrl}/pad-data`;
    
    // Easy Auth expects the parameter to be 'post_login_redirect_uri'
    window.location.href = `${authUrl}?post_login_redirect_uri=${encodeURIComponent(redirectUrl)}`;
  };

  if (checking) {
    return (
      <section className={styles.section}>
        <div className={styles.signIn}>
          <div className={styles.logo}>
            <img src={logoAuth} alt="logo" className={styles.user_img} />
          </div>
          <p>Checking authentication...</p>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <div className={styles.signIn}>
        <header>
          <div className={styles.logo}>
            <img src={logoAuth} alt="logo" className={styles.user_img} />
          </div>
          <p className={styles.heading}>Welcome back</p>
          <p className={styles.subHeading}>Sign in to continue</p>
        </header>

        {error && (
          <div style={{
            padding: '10px',
            marginTop: '20px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Password Login Form - Only shown in local/dev */}
        {authMode === 'password' && (
          <form onSubmit={handlePasswordLogin} style={{ marginTop: '20px' }}>
            <label className={styles.label} htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmailInput(e.target.value)}
              className={styles.input}
              required
            />
            
            <label className={styles.label} htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              required
            />
            
            <button type="submit" className={styles.button}>
              Sign In
            </button>
            
            <p style={{ 
              textAlign: 'center', 
              marginTop: '15px', 
              color: '#666',
              fontSize: '12px',
              padding: '8px',
              backgroundColor: '#fff3cd',
              borderRadius: '4px'
            }}>
              🔧 Local Development Mode
            </p>
          </form>
        )}

        {/* Microsoft Login Button - Shown in Azure */}
        {authMode === 'microsoft' && (
          <>
            <button 
              onClick={handleMicrosoftLogin}
              className={styles.button}
              style={{
                width: '100%',
                padding: '12px 20px',
                backgroundColor: '#7fba00ff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                marginTop: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px'
              }}
            >
              <svg width="21" height="21" viewBox="0 0 21 21" fill="none">
                <rect x="0" y="0" width="10" height="10" fill="#f25022"/>
                <rect x="11" y="0" width="10" height="10" fill="#7fba00"/>
                <rect x="0" y="11" width="10" height="10" fill="#00a4ef"/>
                <rect x="11" y="11" width="10" height="10" fill="#ffb900"/>
              </svg>
              Sign in with Microsoft
            </button>

            <p style={{ 
              textAlign: 'center', 
              marginTop: '20px', 
              color: '#666',
              fontSize: '14px' 
            }}>
              Use your work or personal Microsoft account
            </p>
          </>
        )}
      </div>
    </section>
  );
};

export { SignInForm };